import type { BillingMarket } from "@/types/database";

/**
 * Marchés de facturation RLCore.
 *
 * UN SEUL PRIX OFFICIEL, dans deux devises : l'Europe est facturée en euros,
 * le reste du monde en dollars US. Le montant en dollars n'est pas une
 * conversion au jour le jour — c'est un montant FIXE, arrondi, posé à la main
 * à partir du taux du moment (comme le font les SaaS internationaux), et
 * révisé si l'euro bouge de plus de ~5 %. Il vit dans `src/lib/plans.ts` et
 * doit être égal à `currency_options.usd` du prix Stripe correspondant
 * (`npm run check:prices` le vérifie).
 *
 * Il n'y a plus de tarif réduit par pays : un prospect dominicain voit le prix
 * officiel en dollars. Un geste commercial passe par un coupon Stripe ou le
 * forfait sur mesure — jamais par un marché à part.
 *
 * Module SANS AUCUNE dépendance : il est importé par `src/proxy.ts`, qui tourne
 * en edge. Pas de `next/*`, pas de Supabase, pas d'accès à `process.env`.
 * Miroir à tenir à jour dans `mobile/src/lib/markets.ts`.
 */

export type BillingCurrency = "EUR" | "USD";

export const MARKETS: Record<
  BillingMarket,
  {
    label: string;
    currency: BillingCurrency;
    countries: string[];
  }
> = {
  // Le code `FR` est historique (valeur par défaut de la colonne
  // `organizations.billing_market`) et couvre TOUTE l'Europe : le renommer
  // aurait réécrit chaque org européenne sans rien changer au produit.
  FR: {
    label: "Europe",
    currency: "EUR",
    // Toute l'Europe géographique, zone euro ou non : un Britannique ou un
    // Suisse a des euros sous les yeux tous les jours, pas des dollars.
    countries: [
      // Union européenne
      "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
      "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
      "SI", "ES", "SE",
      // Hors UE
      "GB", "CH", "NO", "IS", "LI", "AD", "MC", "SM", "VA", "GI", "FO", "GL",
      "AL", "BA", "ME", "MK", "RS", "XK", "MD", "UA", "BY", "GE", "AM",
      // Territoires français
      "GP", "MQ", "GF", "RE", "YT", "PM", "BL", "MF", "WF", "PF", "NC",
    ],
  },
  INTL: {
    label: "International",
    currency: "USD",
    // Aucune liste : c'est le marché de tout pays qui n'est pas européen.
    countries: [],
  },
};

/**
 * Repères « agence classique » du comparatif de la page tarifs, DANS LA DEVISE
 * DU MARCHÉ. Un montant en euros n'a rien à dire à un prospect dominicain ou
 * mexicain : il a des devis locaux sous les yeux.
 *
 * Sources (août 2026, à revalider avant toute évolution des chiffres) :
 *  - FR : chiffres RLCore existants.
 *  - INTL : 800 – 3 000 $ pour un site institutionnel complet en Amérique
 *    latine — la fourchette la plus large relevée, donc la moins risquée à
 *    servir à un visiteur dont on ne connaît que le continent.
 *
 * `maintenance` vaut `null` là où aucun taux horaire local fiable n'est
 * documenté : mieux vaut afficher « facturée à part » qu'un chiffre inventé
 * sur une page que lisent des prospects.
 */
export interface AgencyBenchmark {
  /** Investissement initial [min, max]. */
  initial: [number, number];
  /** Hébergement par an [min, max]. */
  hostingPerYear: [number, number];
  /** Taux horaire de maintenance [min, max], ou null si non documenté. */
  maintenancePerHour: [number, number] | null;
}

export const AGENCY_BENCHMARK: Record<BillingMarket, AgencyBenchmark> = {
  FR: {
    initial: [3000, 15000],
    hostingPerYear: [100, 300],
    maintenancePerHour: [50, 150],
  },
  INTL: {
    initial: [800, 3000],
    hostingPerYear: [100, 300],
    maintenancePerHour: null,
  },
};

export const DEFAULT_MARKET: BillingMarket = "FR";

/**
 * Marché du VISITEUR (page tarifs publique uniquement). Sans rapport avec
 * `organizations.billing_market`, qui décide de la facture.
 *
 * DEUX cookies, et c'est délibéré :
 *
 * - `rl_market` MIROITE la géo-IP. Le proxy le réécrit dès que le pays détecté
 *   ne correspond plus. Une seule valeur figée pour un an ne marchait pas : il
 *   suffisait d'une première visite mal géolocalisée — VPN, déploiement en
 *   cours, réseau d'entreprise — pour qu'un visiteur dominicain reste sur les
 *   tarifs en euros à vie, sans aucun moyen de s'en apercevoir.
 *
 * - `rl_market_pin` n'est écrit QUE par le sélecteur de la page tarifs. Sa
 *   présence gèle tout : un choix explicite ne doit jamais être corrigé par
 *   une supposition. C'est la distinction qui manquait — une déduction
 *   automatique était stockée exactement comme une décision humaine.
 *
 * - `rl_country` porte le PAYS détecté (code ISO), pour les rares écrans qui
 *   ciblent un pays et non une devise — la bulle WhatsApp de la page
 *   dominicaine. Le marché ne suffit plus : « International » couvre aussi
 *   bien Saint-Domingue que Tokyo.
 */
export const MARKET_COOKIE = "rl_market";
export const MARKET_PIN_COOKIE = "rl_market_pin";
export const COUNTRY_COOKIE = "rl_country";

/** Marchés dans l'ordre d'affichage (sélecteurs, grille tarifaire). */
export const BILLING_MARKETS = Object.keys(MARKETS) as BillingMarket[];

export function isBillingMarket(value: unknown): value is BillingMarket {
  return typeof value === "string" && value in MARKETS;
}

/**
 * Marché lu depuis une ligne de base, avec repli sûr.
 *
 * `Organization.billing_market` est typé non-nullable, ce que TypeScript ne
 * peut pas vérifier : la colonne n'existe que si la migration 00056 a été
 * appliquée, et ses valeurs `DO`/`LATAM` n'existent plus depuis la 00107. Sur
 * une base en retard, la valeur arrive `undefined` ou périmée,
 * `MARKETS[valeur]` vaut `undefined`, et lire `.currency` dessus faisait
 * tomber TOUTE la fiche organisation de l'admin en production.
 *
 * Un écran d'administration ne doit pas disparaître parce qu'une colonne
 * manque. On retombe donc sur le marché historique — celui de la base
 * installée — en le SIGNALANT : un repli muet redeviendrait invisible, et
 * c'est justement ce qu'on cherche à ne plus faire.
 *
 * À n'utiliser qu'en LECTURE d'affichage. Les chemins qui facturent lisent la
 * colonne directement et doivent échouer bruyamment si elle manque : mieux
 * vaut une erreur qu'un prélèvement dans la mauvaise devise.
 */
export function normalizeMarket(value: unknown, where?: string): BillingMarket {
  if (isBillingMarket(value)) return value;
  console.warn(
    `[markets] billing_market absent ou invalide${where ? ` (${where})` : ""} : ` +
      `repli sur ${DEFAULT_MARKET}. Les migrations 00056 et 00107 sont-elles appliquées sur cette base ?`,
  );
  return DEFAULT_MARKET;
}

export function currencyForMarket(market: BillingMarket): BillingCurrency {
  return MARKETS[market].currency;
}

/**
 * Marché d'un code pays ISO — pour la détection géo-IP de la page tarifs
 * UNIQUEMENT. Ne jamais s'en servir pour la facturation d'une organisation :
 * son marché est une décision explicite, stockée en base.
 *
 * Pays européen → euros ; tout autre pays CONNU → dollars ; pays inconnu
 * (en-tête absent, réseau privé) → la France : c'est le tarif historique,
 * celui de la base installée, et celui dans lequel les CGV sont rédigées.
 */
export function marketForCountry(
  countryCode: string | null | undefined
): BillingMarket {
  if (!countryCode) return DEFAULT_MARKET;
  const code = countryCode.toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return DEFAULT_MARKET;
  return MARKETS.FR.countries.includes(code) ? "FR" : "INTL";
}

/**
 * Montant formaté dans sa devise.
 *
 * La locale de formatage vient de la DEVISE, pas de l'interface : `fr-FR`
 * rend les dollars « 3 200 $US », là où un lecteur international attend
 * « $3,200 ». Seul `en-US` produit la forme universelle.
 */
export function formatMoney(
  amount: number,
  currency: BillingCurrency,
  uiLocale = "fr-FR"
): string {
  const locale = currency === "USD" ? "en-US" : uiLocale;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}
